import { toast } from '@/hooks/use-toast';
import { adminClearMfa, changePassword, createInvitation, createRole, deleteRole, disableUser, enableUser, getInvitations, getPermissions, getRoles, getUserById, getUsers, resendInvitation, resetPassword, revokeInvitation, updateCurrentUser, updateRole, updateUser, uploadAvatar, } from '@/lib/api/admin';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
// ============================================================================
// Permissions Hooks
// ============================================================================
export function usePermissions() {
    return useQuery({
        queryKey: ['permissions'],
        queryFn: getPermissions,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}
// ============================================================================
// Roles Hooks
// ============================================================================
export function useRoles(clinicId) {
    return useQuery({
        queryKey: ['roles', clinicId],
        queryFn: () => getRoles(clinicId),
        staleTime: 60 * 1000, // 1 minute
        enabled: !!clinicId,
    });
}
export function useCreateRole() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request) => createRole(request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] });
            toast({ title: 'Role created', description: 'The role has been created successfully.' });
        },
        onError: () => {
            toast({ title: 'Error', description: 'Failed to create role.', variant: 'destructive' });
        },
    });
}
export function useUpdateRole() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, request }) => updateRole(id, request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] });
            toast({ title: 'Role updated', description: 'The role has been updated successfully.' });
        },
        onError: () => {
            toast({ title: 'Error', description: 'Failed to update role.', variant: 'destructive' });
        },
    });
}
export function useDeleteRole() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => deleteRole(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['roles'] });
            toast({ title: 'Role deleted', description: 'The role has been deleted successfully.' });
        },
        onError: () => {
            toast({ title: 'Error', description: 'Failed to delete role.', variant: 'destructive' });
        },
    });
}
// ============================================================================
// Users Hooks
// ============================================================================
export function useUsers(query = {}) {
    return useQuery({
        queryKey: ['users', query],
        queryFn: () => getUsers(query),
    });
}
export function useUserDetail(userId) {
    return useQuery({
        queryKey: ['user', userId],
        queryFn: () => getUserById(userId),
        enabled: !!userId,
    });
}
export function useUpdateUser() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, request }) => updateUser(id, request),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            queryClient.invalidateQueries({ queryKey: ['user', variables.id] });
            toast({ title: 'User updated', description: 'The user has been updated successfully.' });
        },
        onError: () => {
            toast({ title: 'Error', description: 'Failed to update user.', variant: 'destructive' });
        },
    });
}
export function useDisableUser() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, clinicId }) => disableUser(id, clinicId),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            // Also refetch the open detail panel so its per-clinic status flips live.
            queryClient.invalidateQueries({ queryKey: ['user', variables.id] });
            toast({ title: 'User disabled', description: 'The user has been disabled.' });
        },
        onError: () => {
            toast({ title: 'Error', description: 'Failed to disable user.', variant: 'destructive' });
        },
    });
}
export function useEnableUser() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, clinicId }) => enableUser(id, clinicId),
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
            // Also refetch the open detail panel so its per-clinic status flips live.
            queryClient.invalidateQueries({ queryKey: ['user', variables.id] });
            toast({ title: 'User enabled', description: 'The user has been enabled.' });
        },
        onError: () => {
            toast({ title: 'Error', description: 'Failed to enable user.', variant: 'destructive' });
        },
    });
}
export function useResetPassword() {
    return useMutation({
        mutationFn: ({ id, clinicId }) => resetPassword(id, clinicId),
        onSuccess: () => {
            toast({ title: 'Password reset', description: 'A password reset email has been sent.' });
        },
        onError: () => {
            toast({ title: 'Error', description: 'Failed to reset password.', variant: 'destructive' });
        },
    });
}
export function useAdminClearMfa() {
    // The admin user-management UI lives in the 'settings' namespace; the other
    // hooks in this file predate app-ui i18n and still use English literals — a
    // holistic pass is tracked separately.
    const { t } = useTranslation('settings');
    return useMutation({
        mutationFn: ({ id, clinicId }) => adminClearMfa(id, clinicId),
        onSuccess: () => {
            toast({
                title: t('users.detail.clearMfaSuccessTitle'),
                description: t('users.detail.clearMfaSuccessBody'),
            });
        },
        onError: () => {
            toast({
                title: t('users.detail.clearMfaErrorTitle'),
                description: t('users.detail.clearMfaErrorBody'),
                variant: 'destructive',
            });
        },
    });
}
// ============================================================================
// Current User (My Account) Hooks
// ============================================================================
export function useUpdateCurrentUser() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request) => updateCurrentUser(request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['currentUser'] });
            toast({
                title: 'Profile updated',
                description: 'Your profile has been updated successfully.',
            });
        },
        onError: () => {
            toast({ title: 'Error', description: 'Failed to update profile.', variant: 'destructive' });
        },
    });
}
export function useChangePassword() {
    return useMutation({
        mutationFn: (request) => changePassword(request),
        onSuccess: () => {
            toast({
                title: 'Password changed',
                description: 'Your password has been changed successfully.',
            });
        },
        onError: (error) => {
            const message = error.message.includes('incorrect')
                ? 'Current password is incorrect.'
                : 'Failed to change password.';
            toast({ title: 'Error', description: message, variant: 'destructive' });
        },
    });
}
export function useUploadAvatar() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (file) => uploadAvatar(file),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['currentUser'] });
        },
        onError: () => {
            toast({ title: 'Error', description: 'Failed to upload avatar.', variant: 'destructive' });
        },
    });
}
// ============================================================================
// Invitations Hooks
// ============================================================================
export function useInvitations(status) {
    return useQuery({
        queryKey: ['invitations', status],
        queryFn: () => getInvitations(status),
    });
}
export function useCreateInvitation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (request) => createInvitation(request),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invitations'] });
            toast({ title: 'Invitation sent', description: 'The invitation has been sent successfully.' });
        },
        onError: () => {
            toast({ title: 'Error', description: 'Failed to send invitation.', variant: 'destructive' });
        },
    });
}
export function useResendInvitation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => resendInvitation(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invitations'] });
            toast({ title: 'Invitation resent', description: 'The invitation has been resent.' });
        },
        onError: () => {
            toast({ title: 'Error', description: 'Failed to resend invitation.', variant: 'destructive' });
        },
    });
}
export function useRevokeInvitation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => revokeInvitation(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['invitations'] });
            toast({ title: 'Invitation revoked', description: 'The invitation has been revoked.' });
        },
        onError: () => {
            toast({ title: 'Error', description: 'Failed to revoke invitation.', variant: 'destructive' });
        },
    });
}
